import React, { useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import FolderItem from './FolderItem';
import { addFolder } from '../redux/actions';

const FolderHierarchyPane = () => {
  const dispatch = useDispatch();
  const allFolders = useSelector(state => state.folders.folders);
  const [folderFilterText, setFolderFilterText] = useState('');

  const handleAddTopLevelFolder = () => {
    const folderName = window.prompt('Enter name for new top-level folder:');
    if (folderName && folderName.trim() !== '') {
      dispatch(addFolder(folderName.trim(), null));
    }
  };

  const { visibleFolderIds, finalTopLevelFolders } = useMemo(() => {
    if (!folderFilterText.trim()) {
      const allIds = new Set(allFolders.map(f => f.id)); // All folders are visible if no filter
      const topLevel = allFolders.filter(f => f.parentId === null);
      return { visibleFolderIds: allIds, finalTopLevelFolders: topLevel };
    }

    const lowerFilterText = folderFilterText.toLowerCase();
    const foldersMap = new Map(allFolders.map(f => [f.id, f]));
    const visibleIds = new Set();

    // Helper to add a folder and all its ancestors to visibleIds
    function addWithAncestors(folderId) {
      let current = foldersMap.get(folderId);
      while (current) {
        if (visibleIds.has(current.id)) break;
        visibleIds.add(current.id);
        current = current.parentId ? foldersMap.get(current.parentId) : null;
      }
    }

    // Helper to add a folder and all its descendants to visibleIds
    function addWithDescendants(folderId) {
      const queue = [folderId]; // Start with the folder itself
      const visitedForDescendants = new Set(); // Prevent infinite loops with circular (though not expected)

      while (queue.length > 0) {
        const currentId = queue.shift();

        if (visitedForDescendants.has(currentId) && visibleIds.has(currentId)) continue;

        visibleIds.add(currentId);
        visitedForDescendants.add(currentId);

        allFolders.forEach(f => {
          if (f.parentId === currentId) {
            if(!visitedForDescendants.has(f.id)){
                 queue.push(f.id);
            }
          }
        });
      }
    }

    allFolders.forEach(f => {
      if (f.name.toLowerCase().includes(lowerFilterText)) {
        // If a folder matches, make it, its ancestors, and all its descendants visible
        addWithAncestors(f.id);
        addWithDescendants(f.id);
      }
    });

    const topLevel = allFolders.filter(f => f.parentId === null && visibleIds.has(f.id));
    return { visibleFolderIds: visibleIds, finalTopLevelFolders: topLevel };
  }, [allFolders, folderFilterText]);


  return (
    <div style={{ border: '1px solid green', padding: '10px', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <h2>Folder Hierarchy</h2>
      <button onClick={handleAddTopLevelFolder} style={{ marginBottom: '10px', flexShrink: 0 }}>
        Add Top-Level Folder
      </button>
      <input
        type="text"
        placeholder="Filter folders by name..."
        value={folderFilterText}
        onChange={(e) => setFolderFilterText(e.target.value)}
        style={{ marginBottom: '10px', padding: '5px', width: 'calc(100% - 12px)', border: '1px solid #ccc', borderRadius: '4px' }}
      />
      <div style={{ overflowY: 'auto', flexGrow: 1, backgroundColor: '#f0f2f5', padding: '5px' }}>
        {allFolders.length === 0 && <p>No folders created yet.</p>}
        {allFolders.length > 0 && finalTopLevelFolders.length === 0 && folderFilterText.trim() !== '' && <p>No folders match filter.</p>}
        {/* Message for no top-level folders when no filter is applied and folders exist (should be rare) */}
        {allFolders.length > 0 && finalTopLevelFolders.length === 0 && folderFilterText.trim() === '' && !allFolders.some(f=>f.parentId === null) && <p>No top-level folders exist.</p>}

        {finalTopLevelFolders.map(folder => (
          <FolderItem
            key={folder.id}
            folder={folder}
            allFolders={allFolders}
            level={0}
            visibleFolderIds={visibleFolderIds}
            folderFilterActive={folderFilterText.trim() !== ''} // Pass this down
          />
        ))}
      </div>
    </div>
  );
};

export default FolderHierarchyPane;
